import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
const DAYS_ES = { Mon:'Lun', Tue:'Mar', Wed:'Mié', Thu:'Jue', Fri:'Vie', Sat:'Sáb', Sun:'Dom' }
const MILEAGE_RATE = 0.67 // IRS 2024

function getWeekDates(offset) {
  const now = new Date()
  const dow = now.getDay()
  const mon = new Date(now)
  mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1) + offset * 7)
  return DAYS.map((_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d })
}
function dateKey(d) { return d.toISOString().slice(0,10) }
function wkLabel(dates) {
  return dates[0].toLocaleDateString('es-MX',{month:'short',day:'numeric'}) +
    ' – ' + dates[6].toLocaleDateString('es-MX',{month:'short',day:'numeric',year:'numeric'})
}
function fmtDate(d) {
  return d.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})
}

function generateTimesheetHTML(staff, dates, hours, tips, flatRatesMap, milesPerDay, notes, payRate, payType, dailyRates) {
  // payRate is a display label; dailyRates is {dk: numericRate} for per-day calculation
  const numericRate = typeof payRate === 'number' ? payRate : parseFloat((String(payRate||'0').match(/[\d.]+/)||['0'])[0]) || 0
  const weekEnding = fmtDate(dates[6])
  const rows = dates.map(d => {
    const dk = dateKey(d)
    const h = parseFloat(hours[dk] || 0)
    const t = parseFloat(tips[dk] || 0)
    const mi = parseFloat(milesPerDay[dk] || 0)
    const flat = (flatRatesMap||{})[dk]
    const hasFlatDay = flat !== undefined && flat !== ''
    const dateStr = d.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})
    if (h === 0 && t === 0 && !hasFlatDay && mi === 0) return null
    const dayRate = (dailyRates && dailyRates[dk]) ? dailyRates[dk] : numericRate
    const subtotal = hasFlatDay ? parseFloat(flat)||0 : (payType === 'hourly' ? h * dayRate : dayRate)
    const mileagePay = mi * MILEAGE_RATE
    const total = subtotal + t + mileagePay
    return { dateStr, h, t, mi, mileagePay, subtotal, total, hasFlatDay, dayRate }
  }).filter(Boolean)

  const totalHours = rows.reduce((s,r) => s + r.h, 0)
  const totalMiles = rows.reduce((s,r) => s + r.mi, 0)
  const totalMileagePay = rows.reduce((s,r) => s + r.mileagePay, 0)
  const totalPay = rows.reduce((s,r) => s + r.total, 0)

  const rowsHTML = rows.map(r => `
    <tr>
      <td>${r.dateStr}</td>
      <td style="text-align:center">${r.h > 0 ? r.h+'h' : '—'}</td>
      <td style="text-align:center">${r.hasFlatDay ? 'Flat' : ('$'+r.dayRate.toFixed(2)+'/hr')}</td>
      <td style="text-align:right">${r.subtotal > 0 ? '$'+r.subtotal.toFixed(2) : '—'}</td>
      <td style="text-align:center">${r.t > 0 ? '$'+r.t.toFixed(2) : '—'}</td>
      <td style="text-align:center">${r.mi > 0 ? r.mi+' mi' : '—'}</td>
      <td style="text-align:center">${r.mileagePay > 0 ? '$'+r.mileagePay.toFixed(2) : '—'}</td>
      <td style="text-align:right"><b>$${r.total.toFixed(2)}</b></td>
    </tr>
  `).join('')

  const emptyRows = Math.max(0, 5 - rows.length)
  const emptyHTML = Array(emptyRows).fill(`<tr>${'<td>&nbsp;</td>'.repeat(8)}</tr>`).join('')

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Timesheet — ${staff.name}</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:Arial,sans-serif; padding:32px; color:#202124; max-width:720px; margin:0 auto; }
  .title { font-size:26px; font-weight:bold; color:#e8547a; text-align:center; margin-bottom:4px; }
  .company { font-size:14px; font-weight:bold; color:#c03060; text-align:center; margin-bottom:3px; }
  .address { font-size:12px; color:#5f6368; text-align:center; }
  .divider { height:2px; background:#e8547a; margin:16px 0; }
  .info-row { display:flex; gap:24px; margin-bottom:20px; }
  .info-field { flex:1; }
  .info-label { font-size:12px; font-weight:bold; }
  .info-value { font-size:13px; border-bottom:1px solid #202124; padding-bottom:2px; margin-top:2px; min-height:20px; }
  .section-label { font-size:13px; font-weight:bold; margin-bottom:8px; }
  table { width:100%; border-collapse:collapse; font-size:11px; margin-bottom:16px; }
  thead tr { background:#e8547a; color:white; }
  thead th { padding:6px 6px; text-align:center; font-weight:bold; }
  thead th:first-child { text-align:left; }
  tbody tr:nth-child(even) { background:#fdf0f3; }
  tbody td { padding:5px 6px; border:0.5px solid #f0c0cc; }
  .total-row td { background:#fdeef3; font-weight:600; border-top:2px solid #e8547a; }
  .total-section { border-top:2px solid #e8547a; padding-top:10px; display:flex; align-items:baseline; gap:12px; margin-bottom:16px; }
  .total-label { font-size:14px; font-weight:bold; }
  .total-amount { font-size:22px; font-weight:bold; color:#e8547a; }
  .breakdown { font-size:11px; color:#777; }
  .notes-box { border:1px solid #bdc1c6; border-radius:6px; padding:10px; min-height:70px; font-size:12px; color:#5f6368; }
  .sig-row { display:flex; gap:40px; margin-top:24px; }
  .sig-line { flex:1; border-top:1px solid #202124; padding-top:5px; font-size:11px; color:#5f6368; }
  @media print { body { padding:16px; } }
</style>
</head>
<body>
  <div class="title">TIME SHEET</div>
  <div class="company">Kitty's Cleaning Services</div>
  <div class="address">6427 Wilcox Ave Bell, CA 90201 &nbsp;·&nbsp; (310) 425-3195</div>
  <div class="divider"></div>
  <div class="info-row">
    <div class="info-field"><div class="info-label">Employee Name:</div><div class="info-value">${staff.name}</div></div>
    <div class="info-field"><div class="info-label">Week Ending:</div><div class="info-value">${weekEnding}</div></div>
  </div>
  <div class="section-label">Hours Log:</div>
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Hours</th><th>Rate</th><th>Subtotal</th><th>Tips</th><th>Miles</th><th>Mile Pay</th><th>Total</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHTML}
      ${emptyHTML}
      <tr class="total-row">
        <td>TOTALS</td>
        <td style="text-align:center">${totalHours}h</td>
        <td></td>
        <td style="text-align:right">—</td>
        <td style="text-align:center">—</td>
        <td style="text-align:center">${totalMiles} mi</td>
        <td style="text-align:center">$${totalMileagePay.toFixed(2)}</td>
        <td style="text-align:right"><b>$${totalPay.toFixed(2)}</b></td>
      </tr>
    </tbody>
  </table>
  <div class="total-section">
    <div class="total-label">Total Pay:</div>
    <div class="total-amount">$${totalPay.toFixed(2)}</div>
    <div class="breakdown">(${totalHours}h worked · ${totalMiles} mi @ $${MILEAGE_RATE}/mi)</div>
  </div>
  <div class="section-label">Notes:</div>
  <div class="notes-box">${(notes||'').replace(/\n/g,'<br>')}</div>
  <div class="sig-row">
    <div class="sig-line">Employee Signature: _________________________</div>
    <div class="sig-line">Date: _______________</div>
  </div>
  <script>window.onload = () => window.print()</script>
</body>
</html>`
}

export default function Payroll() {
  const [weekOffset, setWeekOffset] = useState(0)
  const [staff, setStaff] = useState([])
  const [timesheets, setTimesheets] = useState({})
  const [driversPerDay, setDriversPerDay] = useState({}) // { staffId: { date: true/false } }
  const [tipsData, setTipsData] = useState({})
  const [flatRates, setFlatRates] = useState({})
  const [milesPerDay, setMilesPerDay] = useState({}) // { staffId: { dk: miles } }
  const [notes, setNotes] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generated, setGenerated] = useState(null)

  const dates = getWeekDates(weekOffset)
  const monStr = dateKey(dates[0])
  const sunStr = dateKey(dates[6])

  const fetchData = useCallback(async () => {
    setLoading(true)
    const [{ data: st }, { data: pay }, { data: routes }, { data: weekJobs }] = await Promise.all([
      supabase.from('staff').select('*').eq('is_active', true).order('name'),
      supabase.from('payroll').select('*').gte('period_start', monStr).lte('period_end', sunStr),
      supabase.from('routes').select('*').gte('route_date', monStr).lte('route_date', sunStr),
      supabase.from('jobs').select('*').gte('scheduled_date', monStr).lte('scheduled_date', sunStr),
    ])
    setStaff(st || [])

    const ts = {}, td = {}, fr = {}, mi = {}, ns = {}
    ;(st||[]).forEach(s => {
      ts[s.id] = {}; td[s.id] = {}; fr[s.id] = {}; mi[s.id] = {}; ns[s.id] = ''
      dates.forEach(d => { ts[s.id][dateKey(d)] = 0; td[s.id][dateKey(d)] = 0; mi[s.id][dateKey(d)] = 0 })
    })

    // Step 1: Auto-populate hours from published Plan del Dia routes
    const dpd = {} // driversPerDay: { staffId: { date: true } }
    if (routes && weekJobs) {
      routes.filter(r => r.label !== '__draft__').forEach(r => {
        let staffIds = [], drivers = []
        try { 
          const parsed = JSON.parse(r.notes||'{}')
          staffIds = parsed.staffIds || []
          drivers = parsed.drivers || []
        } catch(e) {}
        const dayJobs = weekJobs.filter(j => j.scheduled_date === r.route_date && j.route === r.label)
        const dayHours = dayJobs.reduce((sum, j) => sum + (parseFloat(j.hours)||0), 0)
        staffIds.forEach(sid => {
          if (!ts[sid]) return
          ts[sid][r.route_date] = (ts[sid][r.route_date]||0) + dayHours
          // Track who drove
          if (!dpd[sid]) dpd[sid] = {}
          if (drivers.includes(sid)) dpd[sid][r.route_date] = true
          else if (dpd[sid][r.route_date] === undefined) dpd[sid][r.route_date] = false
        })
      })
    }
    setDriversPerDay(dpd)

    // Step 1.5: overlay the "Horas a pagar" decided in Actividad → Horas —
    // this is the reconciled number (calculated vs approved vs clocked), so it's
    // a better baseline than the raw route-calculated hours from Step 1.
    // Manual edits made directly here in Payroll (Step 2) can still override it.
    const { data: dailyHours } = await supabase.from('payroll_daily_hours').select('*')
      .gte('date', monStr).lte('date', sunStr)
    ;(dailyHours||[]).forEach(d => {
      if (ts[d.staff_id] && d.hours != null) ts[d.staff_id][d.date] = parseFloat(d.hours) || 0
    })

    // Step 2: Overlay saved payroll data — but only override if value was explicitly saved (not 0)
    if (pay && pay.length > 0) {
      pay.forEach(p => {
        try {
          const data = JSON.parse(p.notes||'{}')
          if (data.timesheet) {
            Object.entries(data.timesheet).forEach(([dk, val]) => {
              if (parseFloat(val) > 0) ts[p.staff_id][dk] = val
            })
          }
          if (data.tips) td[p.staff_id] = { ...td[p.staff_id], ...data.tips }
          if (data.flatRates) fr[p.staff_id] = { ...fr[p.staff_id], ...data.flatRates }
          if (data.milesPerDay) mi[p.staff_id] = { ...mi[p.staff_id], ...data.milesPerDay }
          if (data.note) ns[p.staff_id] = data.note
        } catch(e) {}
      })
    }

    setTimesheets(ts); setTipsData(td); setFlatRates(fr); setMilesPerDay(mi); setNotes(ns)
    setLoading(false)
  }, [weekOffset])

  useEffect(() => { fetchData() }, [fetchData])

  function updateHours(staffId, dk, val) {
    const num = val === '' ? 0 : parseFloat(val) || 0
    setTimesheets(prev => ({ ...prev, [staffId]: { ...prev[staffId], [dk]: num } }))
  }
  function updateTips(staffId, dk, val) {
    const num = val === '' ? 0 : parseFloat(val) || 0
    setTipsData(prev => ({ ...prev, [staffId]: { ...prev[staffId], [dk]: num } }))
  }
  function updateMiles(staffId, dk, val) {
    const num = val === '' ? 0 : parseFloat(val) || 0
    setMilesPerDay(prev => ({ ...prev, [staffId]: { ...prev[staffId], [dk]: num } }))
  }
  function updateFlat(staffId, dk, val) {
    setFlatRates(prev => ({ ...prev, [staffId]: { ...prev[staffId], [dk]: val } }))
  }

  function totalHours(staffId) {
    return Object.values(timesheets[staffId]||{}).reduce((s,h)=>s+(parseFloat(h)||0),0)
  }
  function totalMiles(staffId) {
    return Object.values(milesPerDay[staffId]||{}).reduce((s,m)=>s+(parseFloat(m)||0),0)
  }

  function calcPay(s) {
    const tips = Object.values(tipsData[s.id]||{}).reduce((a,b)=>a+(parseFloat(b)||0),0)
    const sf = flatRates[s.id] || {}
    let base = 0
    dates.forEach(d => {
      const dk = dateKey(d)
      const flat = sf[dk]
      if (flat !== undefined && flat !== '') {
        base += parseFloat(flat)||0
      } else {
        const hrs = parseFloat(timesheets[s.id]?.[dk]||0)
        const miles = parseFloat(milesPerDay[s.id]?.[dk]||0)
        if (s.pay_type_v2 === 'base_mileage') {
          // Base hourly + mileage rate
          base += hrs * parseFloat(s.pay_rate||0)
          // mileage handled separately below
        } else if (s.pay_type_v2 === 'driver_passenger') {
          // Use driversPerDay to determine rate — fall back to miles if no route data
          const driverRate = parseFloat(s.driver_rate||0) || parseFloat(s.pay_rate||0)
          const passengerRate = parseFloat(s.passenger_rate||0) || parseFloat(s.pay_rate||0)
          const drove = driversPerDay[s.id]?.[dk] === true || (driversPerDay[s.id]?.[dk] === undefined && miles > 0)
          const rate = drove ? driverRate : passengerRate
          base += hrs * rate
        } else {
          // Legacy hourly
          base += s.pay_type==='hourly' ? hrs*parseFloat(s.pay_rate||0) : 0
        }
      }
    })
    if (s.pay_type!=='hourly' && s.pay_type_v2 !== 'driver_passenger' && Object.keys(sf).length===0) base = parseFloat(s.pay_rate||0)
    const mileageRate = s.pay_type_v2==='base_mileage' ? parseFloat(s.mileage_rate||0.67) : MILEAGE_RATE
    const mileagePay = totalMiles(s.id) * mileageRate
    return base + tips + mileagePay
  }

  async function saveAll() {
    setSaving(true)
    const totalWeekPay = staff.reduce((s,x)=>s+calcPay(x),0)
    for (const s of staff) {
      const hrs = totalHours(s.id)
      const mi = totalMiles(s.id)
      const pay = calcPay(s)
      const mileagePay = mi * MILEAGE_RATE
      await supabase.from('payroll').upsert({
        staff_id: s.id, period_start: monStr, period_end: sunStr,
        hours_actual: hrs, hours_planned: hrs,
        base_pay: pay - mileagePay, mileage_pay: mileagePay, total_pay: pay,
        notes: JSON.stringify({
          timesheet: timesheets[s.id], tips: tipsData[s.id],
          flatRates: flatRates[s.id], milesPerDay: milesPerDay[s.id], note: notes[s.id],
          rateSnapshot: s.pay_rate, payTypeSnapshot: s.pay_type
        }),
      }, { onConflict: 'staff_id,period_start' })
    }
    // Auto-log payroll as gasto in Finanzas (always, even if 0)
    // Delete existing payroll entry for this week to avoid duplicates
    await supabase.from('financials')
      .delete()
      .eq('category','Nómina')
      .gte('entry_date', monStr)
      .lte('entry_date', sunStr)
    if (totalWeekPay > 0) {
      await supabase.from('financials').insert([{
        type: 'expense',
        category: 'Nómina',
        amount: totalWeekPay,
        description: `Nómina semana ${monStr} — ${staff.length} empleadas`,
        entry_date: monStr,
      }])
    }
    setSaving(false)
    alert(`✓ Nómina guardada y registrada en Finanzas ($${totalWeekPay.toFixed(2)})`)
  }

  function generatePDF(st) {
    const driverRate = parseFloat(st.driver_rate||0) || parseFloat(st.pay_rate||0)
    const passengerRate = parseFloat(st.passenger_rate||0) || parseFloat(st.pay_rate||0)
    // Build per-day rates based on who drove
    const dailyRates = {}
    dates.forEach(d => {
      const dk = dateKey(d)
      if (st.pay_type_v2 === 'driver_passenger') {
        const drove = driversPerDay[st.id]?.[dk] === true
        dailyRates[dk] = drove ? driverRate : passengerRate
      } else {
        dailyRates[dk] = parseFloat(st.pay_rate||0)
      }
    })
    const rateLabel = st.pay_type_v2 === 'driver_passenger'
      ? `Conductora: $${driverRate}/hr · Pasajera: $${passengerRate}/hr`
      : `$${parseFloat(st.pay_rate||0)}/hr`
    const html = generateTimesheetHTML(
      st, dates, timesheets[st.id]||{}, tipsData[st.id]||{},
      flatRates[st.id]||{}, milesPerDay[st.id]||{},
      notes[st.id]||'', rateLabel, st.pay_type, dailyRates
    )
    const win = window.open('','_blank')
    win.document.write(html)
    win.document.close()
    setGenerated(st.name)
  }

  function generateAllPDFs() {
    staff.forEach((st, i) => setTimeout(() => generatePDF(st), i * 800))
  }

  const totalWeekPay = staff.reduce((s,x)=>s+calcPay(x),0)

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <button style={s.nbtn} onClick={()=>setWeekOffset(w=>w-1)}>←</button>
        <span style={s.weekLbl}>{wkLabel(dates)}</span>
        <button style={s.nbtn} onClick={()=>setWeekOffset(w=>w+1)}>→</button>
        <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
          {saving&&<span style={{fontSize:11,color:'#80868b'}}>Guardando...</span>}
          <button style={s.saveBtn} onClick={saveAll} disabled={saving}>Guardar semana</button>
          <button style={s.pdfAllBtn} onClick={generateAllPDFs}>📄 PDF todos</button>
        </div>
      </div>

      {loading ? <div style={{padding:32,textAlign:'center',color:'#9aa0a6'}}>Cargando...</div> : (
        <div style={s.body}>
          <div style={s.summaryRow}>
            <div style={s.sumCard}><div style={s.sumNum}>{staff.length}</div><div style={s.sumLbl}>Empleadas</div></div>
            <div style={s.sumCard}><div style={s.sumNum}>{staff.reduce((s,x)=>s+totalHours(x.id),0)}h</div><div style={s.sumLbl}>Horas totales</div></div>
            <div style={s.sumCard}><div style={s.sumNum}>{Math.round(staff.reduce((s,x)=>s+totalMiles(x.id),0))} mi</div><div style={s.sumLbl}>Millas totales</div></div>
            <div style={{...s.sumCard}}><div style={{...s.sumNum,color:'#e8547a'}}>${totalWeekPay.toFixed(2)}</div><div style={s.sumLbl}>Nómina total</div></div>
          </div>

          <div style={{padding:'6px 14px',background:'#f8f9fa',borderRadius:8,fontSize:11,color:'#80868b',marginBottom:10,display:'flex',gap:16,flexWrap:'wrap'}}>
            <span>⬜ <b>hrs</b> = horas</span>
            <span style={{color:'#7F77DD'}}>🟪 <b>$flat</b> = tarifa plana ese día</span>
            <span style={{color:'#1d9e75'}}>🟩 <b>tip</b> = propina</span>
            <span style={{color:'#185fa5'}}>🟦 <b>mi</b> = millas conducidas ese día</span>
          </div>

          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Empleada</th>
                  <th style={s.th}>Tarifa</th>
                  {dates.map((d,i)=>(
                    <th key={i} style={{...s.th,textAlign:'center',minWidth:68}}>
                      <div>{DAYS_ES[DAYS[i]]}</div>
                      <div style={{fontSize:9,color:'#9aa0a6',fontWeight:400}}>{d.getDate()}</div>
                    </th>
                  ))}
                  <th style={{...s.th,textAlign:'center'}}>Hrs</th>
                  <th style={{...s.th,textAlign:'center'}}>Mi</th>
                  <th style={{...s.th,textAlign:'right'}}>Total</th>
                  <th style={{...s.th,textAlign:'center'}}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {staff.map(st=>{
                  const pay=calcPay(st)
                  const mi=totalMiles(st.id)
                  return (
                    <React.Fragment key={st.id}>
                      <tr style={{borderBottom:'1px solid #e3e5e8'}}>
                        <td style={s.td}>
                          <div style={{fontWeight:500,fontSize:13}}>{st.name}</div>
                          <div style={{fontSize:10,color:'#9aa0a6'}}>{st.role==='lead'?'Líder':'Miembro'}</div>
                        </td>
                        <td style={{...s.td,fontSize:11,color:'#5f6368'}}>
                          <div>${st.pay_rate}</div>
                          <div style={{fontSize:9,color:'#9aa0a6'}}>{st.pay_type==='hourly'?'/hr':'fijo'}</div>
                        </td>
                        {dates.map((d,di)=>{
                          const dk=dateKey(d)
                          const hrs=timesheets[st.id]?.[dk]||0
                          const tip=tipsData[st.id]?.[dk]||0
                          const flat=flatRates[st.id]?.[dk]
                          const hasFlatDay=flat!==undefined&&flat!==''
                          const miles=milesPerDay[st.id]?.[dk]||0
                          return(
                            <td key={di} style={{...s.td,textAlign:'center',padding:'5px 2px',verticalAlign:'top'}}>
                              <input type="number" min="0" max="12" step="0.5" value={hrs||''} placeholder="hrs"
                                onChange={e=>updateHours(st.id,dk,e.target.value)}
                                style={{...s.cellInput,opacity:hasFlatDay?0.3:1}}/>
                              <input type="number" min="0" step="5" value={flat??''} placeholder="$flat"
                                onChange={e=>updateFlat(st.id,dk,e.target.value)}
                                style={{...s.cellInput,color:'#7F77DD',borderColor:hasFlatDay?'#7F77DD':'#eee',background:hasFlatDay?'#EEEDFE':'#f8f9fa',fontWeight:hasFlatDay?600:400}}
                                title="Tarifa plana — reemplaza horas ese día"/>
                              <input type="number" min="0" step="1" value={tip||''} placeholder="tip"
                                onChange={e=>updateTips(st.id,dk,e.target.value)}
                                style={{...s.cellInput,color:'#1d9e75',borderColor:tip>0?'#5DCAA5':'#eee'}}/>
                              <input type="number" min="0" step="1" value={miles||''} placeholder="mi"
                                onChange={e=>updateMiles(st.id,dk,e.target.value)}
                                style={{...s.cellInput,color:'#185fa5',borderColor:miles>0?'#185fa5':'#eee',background:miles>0?'#e6f1fb':'#f8f9fa'}}
                                title="Millas conducidas este día"/>
                              {miles>0&&<div style={{fontSize:8,color:'#185fa5'}}>${(miles*MILEAGE_RATE).toFixed(2)}</div>}
                            </td>
                          )
                        })}
                        <td style={{...s.td,textAlign:'center',fontWeight:600,fontSize:13}}>
                          {totalHours(st.id)>0?`${totalHours(st.id)}h`:'—'}
                        </td>
                        <td style={{...s.td,textAlign:'center',fontSize:12,color:'#185fa5'}}>
                          {mi>0?`${Math.round(mi)} mi`:'—'}
                        </td>
                        <td style={{...s.td,textAlign:'right',fontWeight:600,color:'#e8547a',fontSize:14}}>
                          {pay>0?`$${pay.toFixed(2)}`:'—'}
                        </td>
                        <td style={{...s.td,textAlign:'center'}}>
                          <button style={s.pdfBtn} onClick={()=>generatePDF(st)}>📄</button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={dates.length+6} style={{padding:'4px 12px 8px',borderBottom:'1px solid #e3e5e8'}}>
                          <input style={s.noteInput} value={notes[st.id]||''}
                            placeholder={`Nota para ${st.name}`}
                            onChange={e=>setNotes(prev=>({...prev,[st.id]:e.target.value}))}/>
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
                <tr style={{background:'#fdf0f3',fontWeight:600}}>
                  <td colSpan={2} style={{...s.td,borderTop:'2px solid #e8547a'}}>Total general</td>
                  {dates.map((_,i)=><td key={i} style={{...s.td,borderTop:'2px solid #e8547a'}}/>)}
                  <td style={{...s.td,textAlign:'center',borderTop:'2px solid #e8547a'}}>{staff.reduce((s,x)=>s+totalHours(x.id),0)}h</td>
                  <td style={{...s.td,textAlign:'center',borderTop:'2px solid #e8547a',color:'#185fa5'}}>{Math.round(staff.reduce((s,x)=>s+totalMiles(x.id),0))} mi</td>
                  <td style={{...s.td,textAlign:'right',borderTop:'2px solid #e8547a',color:'#e8547a'}}>${totalWeekPay.toFixed(2)}</td>
                  <td style={{...s.td,borderTop:'2px solid #e8547a'}}/>
                </tr>
              </tbody>
            </table>
          </div>

          {generated&&(
            <div style={{margin:'10px 0',padding:'10px 14px',background:'#fdeef3',borderRadius:8,fontSize:12,color:'#c03060'}}>
              ✓ Timesheet de {generated} abierto — usa Cmd+P para guardar como PDF.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:8,padding:'9px 16px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0},
  nbtn:{padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',cursor:'pointer',fontSize:14,color:'#5f6368'},
  weekLbl:{fontSize:13,fontWeight:500,color:'#202124'},
  saveBtn:{fontSize:12,padding:'6px 14px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#5f6368',cursor:'pointer',fontWeight:500},
  pdfAllBtn:{fontSize:12,padding:'6px 14px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',cursor:'pointer',fontWeight:500},
  pdfBtn:{fontSize:14,padding:'3px 8px',border:'1px solid #dadce0',borderRadius:6,background:'#fdeef3',cursor:'pointer'},
  body:{flex:1,overflowY:'auto',padding:'14px 16px'},
  summaryRow:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:12},
  sumCard:{background:'#fff',borderRadius:12,padding:'12px 14px',border:'1px solid #e3e5e8'},
  sumNum:{fontSize:22,fontWeight:600,color:'#202124',lineHeight:1},
  sumLbl:{fontSize:11,color:'#80868b',marginTop:4},
  tableWrap:{background:'#fff',borderRadius:12,border:'1px solid #e3e5e8',overflow:'auto'},
  table:{width:'100%',borderCollapse:'collapse'},
  th:{padding:'10px 6px',background:'#fdf0f3',borderBottom:'1px solid #e3e5e8',fontSize:11,fontWeight:600,color:'#5f6368',textAlign:'left'},
  td:{padding:'8px 8px',fontSize:13,color:'#202124',verticalAlign:'middle'},
  cellInput:{width:48,textAlign:'center',padding:'3px 2px',border:'1px solid #e3e5e8',borderRadius:5,fontSize:11,background:'#f8f9fa',color:'#202124',display:'block',margin:'0 auto 2px'},
  noteInput:{width:'100%',fontSize:11,padding:'4px 8px',border:'1px solid #e3e5e8',borderRadius:6,background:'#f8f9fa',color:'#5f6368'},
}
